import { useMemo, useState } from "react";
import { UsePilotStatsResult } from "../hooks/usePilotStats";
import { formatDurationHoursRounded } from "../utils/formatters";
import {
  tDcsUnitCategory,
  tDcsUnitDomain,
  tDcsUnitFamily,
  tDcsWeaponFamily
} from "../i18n";

// Rows shown before "Show all"; the community lists run to 40+ entries.
const COLLAPSED_ROWS = 10;
// Anything under half an hour would print as "0h".
const MIN_FLIGHT_SECONDS = 1800;

const GAUGE_LABELS: Record<string, string> = {
  most_ground_kills_in_sortie: "Most Ground Kills in a Sortie",
  most_air_kills_in_sortie: "Most Air Kills in a Sortie",
  longest_refuel_contact_seconds: "Longest Refuel Contact (seconds)",
  longest_missile_hit_nm: "Longest Missile Hit (nm)",
  highest_speed_mach: "Highest Speed (Mach)",
  highest_altitude_ft: "Highest Altitude Reached (ft)",
  most_armored_kills_in_sortie: "Most Armored Kills in Sortie",
  most_sead_kills_in_sortie: "Most SEAD Kills in Sortie",
  longest_sortie_distance_nm: "Longest Sortie Distance (nm)",
  longest_noe_distance_nm: "Longest NOE Distance (nm)",
};

export const PILOT_GAUGE_IDS = Object.keys(GAUGE_LABELS);

function normalizeVictimDomain(value: unknown): string {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "a2a") return "air";
  if (raw === "a2g") return "ground";
  if (raw === "surface") return "ground";
  if (raw === "land") return "ground";
  return raw;
}

function displayMetadata(value: unknown, translate: (key: string) => string) {
  const raw = String(value ?? "").trim();
  if (!raw || raw.toLowerCase() === "unknown") return "Unknown";
  return translate(raw);
}

interface PilotStatsTabProps {
  pilotUid: string | null;
  stats: UsePilotStatsResult;
  // Community totals use the same cards as a pilot's own counters.
  community?: boolean;
  // The median pilot, shown beside a pilot's own kills and flight time. Landings are left out: each
  // median is over a different set of pilots, so the field and carrier figures do not add up.
  typical?: { kills: number; durationSeconds: number } | null;
  showHighScores?: boolean;
}

const PilotStatsTab = ({ pilotUid, stats, community = false, typical = null, showHighScores = true }: PilotStatsTabProps) => {
  const { data, loading, error, refresh } = stats;

  const hasAnyData = useMemo(() => {
    if (!data) return false;
    return Boolean(
      data.totalKills ||
        data.totalDuration ||
        data.totalLandings ||
        data.carrierLandings ||
        data.killsByAirframe.length ||
        data.killsByWeaponFamily.length ||
        data.killsByVictimDomainAndCategory.length ||
        data.durationByAirframe.length ||
        data.landingsByType.length ||
        data.gauges.length
    );
  }, [data]);

  const highScoreItems = useMemo(() => {
    if (!data) return [] as Array<{ key: string; label?: string; value: string; valueNumber?: number }>;

    const byGaugeId = new Map(data.gauges.map((gauge) => [gauge.gaugeId, gauge]));

    return PILOT_GAUGE_IDS.reduce<Array<{ key: string; label?: string; value: string; valueNumber?: number }>>((items, gaugeId) => {
        const gauge = byGaugeId.get(gaugeId);
        if (!gauge || !Number.isFinite(gauge.value)) return items;

        items.push({
          key: gaugeId,
          label: GAUGE_LABELS[gaugeId] ?? gaugeId,
          value: formatGaugeValue(gaugeId, gauge.value),
          valueNumber: gauge.value,
        });

        return items;
      }, []);
  }, [data]);

  const overview = useMemo(() => {
    if (!data) return null;

    const kills = data.totalKills?.event_count ?? 0;
    const durationSeconds = data.totalDuration?.value_total ?? 0;
    const landings = data.totalLandings?.event_count ?? 0;
    const carrierLandings = data.carrierLandings?.event_count ?? 0;

    const fieldLandings = data.landingsByType
      .filter((row) => String(row.tags.airdrome_type ?? "").toLowerCase() !== "carrier")
      .reduce((sum, row) => sum + (row.event_count ?? 0), 0);

    return { kills, durationSeconds, landings, carrierLandings, fieldLandings };
  }, [data]);

  if (!pilotUid && !community) {
    return <div className="empty-state">Select a pilot to view stats.</div>;
  }

  return (
    <>
      {error && (
        <div className="alert">
          <span>{error}</span>
          <button type="button" onClick={refresh}>
            Retry
          </button>
        </div>
      )}

      {loading && <div className="status-area"><span className="status">Loading…</span></div>}

      {data && !loading && !hasAnyData && (
        <div className="empty-state">{community ? "No community stats yet." : "No stats found for this pilot yet."}</div>
      )}

      {data && hasAnyData && (
        <div className="stats">
          <div className="stats-overview">
            <div className="stats-card">
              <div className="stats-card-label">Total Kills</div>
              <div className="stats-card-value stats-card-value-large">{overview?.kills ?? 0}</div>
              {typical && <div className="stats-card-typical">Typical pilot: {Math.round(typical.kills).toLocaleString()}</div>}
            </div>
            <div className="stats-card">
              <div className="stats-card-label">Flight Time</div>
              <div className="stats-card-value stats-card-value-large">
                {formatDurationHoursRounded(overview?.durationSeconds ?? 0)}
              </div>
              {typical && <div className="stats-card-typical">Typical pilot: {formatDurationHoursRounded(typical.durationSeconds)}</div>}
            </div>
            <div className="stats-card">
              <div className="stats-card-label">Landings</div>
              <div className="stats-card-split">
                <div className="stats-card-split-cell stats-card-split-left">
                  <div className="stats-card-split-label">Field</div>
                  <div className="stats-card-split-value">{overview?.fieldLandings ?? 0}</div>
                </div>
                <div className="stats-card-split-cell stats-card-split-right">
                  <div className="stats-card-split-label">Carrier</div>
                  <div className="stats-card-split-value">{overview?.carrierLandings ?? 0}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="stats-sections">
            <StatsSection
              title="Flight Time by Airframe"
              items={data.durationByAirframe.filter((row) => row.value_total >= MIN_FLIGHT_SECONDS).map((row) => ({
                key: String(row.tags.unit_family ?? "Unknown"),
                label: displayMetadata(row.tags.unit_family, tDcsUnitFamily),
                value: formatDurationHoursRounded(row.value_total),
                valueNumber: row.value_total,
              }))}
            />

            <StatsSection
              title="Kills by Airframe"
              items={data.killsByAirframe.map((row) => ({
                key: String(row.tags.killer_unit_family ?? "Unknown"),
                label: displayMetadata(row.tags.killer_unit_family, tDcsUnitFamily),
                value: String(row.event_count),
                valueNumber: row.event_count,
              }))}
            />

            <StatsSection
              title="Top Weapons"
              items={otherLast(data.killsByWeaponFamily.map((row) => ({
                key: String(row.tags.weapon_family ?? "Unknown"),
                label: displayMetadata(row.tags.weapon_family, tDcsWeaponFamily),
                value: String(row.event_count),
                valueNumber: row.event_count,
              })))}
            />

            {showHighScores && (
              <StatsSection
                title="High Scores"
                items={highScoreItems}
                emptyMessage="No high scores tracked yet for this pilot."
                showBars={false}
                compactRows={true}
              />
            )}

            <KillsByTargetSection killsByVictimDomainAndCategory={data.killsByVictimDomainAndCategory} />
          </div>
        </div>
      )}
    </>
  );
};

type StatsItem = { key: string; label?: string; value: string; valueNumber?: number };

// Two raw categories can translate to the same label; show one row with the sum.
function mergeByLabel(items: StatsItem[]): StatsItem[] {
  const merged = new Map<string, StatsItem>();
  items.forEach((item) => {
    const label = item.label ?? item.key;
    const existing = merged.get(label);
    if (!existing) {
      merged.set(label, { ...item });
      return;
    }
    existing.valueNumber = (existing.valueNumber ?? 0) + (item.valueNumber ?? 0);
    existing.value = String(existing.valueNumber);
  });
  return Array.from(merged.values()).sort((a, b) => (b.valueNumber ?? 0) - (a.valueNumber ?? 0));
}

// "Other" is a catch-all that outranks every real weapon; it reads better at the end.
function otherLast(items: StatsItem[]): StatsItem[] {
  const isOther = (item: StatsItem) => item.key.toLowerCase() === "other";
  return [...items.filter((item) => !isOther(item)), ...items.filter(isOther)];
}

function KillsByTargetSection({
  killsByVictimDomainAndCategory,
}: {
  killsByVictimDomainAndCategory: Array<{
    tags: Record<string, unknown>;
    event_count: number;
  }>;
}) {
  const targetItems = (domain: string) => mergeByLabel(
    killsByVictimDomainAndCategory
      .filter((row) => normalizeVictimDomain(row.tags.victim_unit_domain) === domain)
      .map((row) => ({
        key: String(row.tags.victim_unit_category ?? "unknown"),
        label: displayMetadata(row.tags.victim_unit_category, tDcsUnitCategory),
        value: String(row.event_count),
        valueNumber: row.event_count,
      }))
  );
  const airItems = targetItems("air");
  const groundItems = targetItems("ground");

  const otherItems = killsByVictimDomainAndCategory
    .filter((row) => {
      const domain = normalizeVictimDomain(row.tags.victim_unit_domain);
      return domain !== "air" && domain !== "ground";
    })
    .map((row) => {
      const domain = normalizeVictimDomain(row.tags.victim_unit_domain) || "unknown";
      const category = String(row.tags.victim_unit_category ?? "unknown");
      const domainLabel = displayMetadata(domain, tDcsUnitDomain);
      const categoryLabel = displayMetadata(category, tDcsUnitCategory);
      return {
        key: `${domain} / ${category}`,
        label: `${domainLabel} / ${categoryLabel}`,
        value: String(row.event_count),
        valueNumber: row.event_count
      };
    });

  // Hide the whole container if there's no target breakdown at all.
  if (airItems.length === 0 && groundItems.length === 0 && otherItems.length === 0) return null;

  return (
    <section className="stats-section stats-section-group">
      <header className="stats-section-header">
        <h2 className="stats-section-title">Kills by Target</h2>
      </header>

      <div className="stats-subsections">
        <div className="stats-subsection stats-subsection-left">
          <div className="stats-subsection-title">Air-to-air</div>
          <StatsSubsectionList items={airItems} emptyMessage="No air target metadata yet (victim domain/category missing)." />
        </div>

        <div className="stats-subsection stats-subsection-right">
          <div className="stats-subsection-title">Ground</div>
          <StatsSubsectionList items={groundItems} emptyMessage="No ground target metadata yet (victim domain/category missing)." />
        </div>

        {otherItems.length > 0 ? (
          <div className="stats-subsection stats-subsection-span">
            <div className="stats-subsection-title">Other</div>
            <StatsSubsectionList items={otherItems} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function StatsSubsectionList({
  items,
  emptyMessage,
}: {
  items: Array<{ key: string; label?: string; value: string; valueNumber?: number }>;
  emptyMessage?: string;
}) {
  if (items.length === 0 && !emptyMessage) return null;

  const maxValue = items.reduce((max, item) => {
    const value = item.valueNumber;
    if (typeof value !== "number" || !Number.isFinite(value)) return max;
    return Math.max(max, value);
  }, 0);

  if (items.length === 0) {
    return <div className="empty-state">{emptyMessage}</div>;
  }

  return (
    <div className="stats-list" role="list">
      {items.map((item) => (
        <div key={item.key} className="stats-row" role="listitem">
          {typeof item.valueNumber === "number" && Number.isFinite(item.valueNumber) && maxValue > 0 ? (
            <div
              className="stats-row-bar"
              style={{ width: `${Math.max(0, Math.min(100, (item.valueNumber / maxValue) * 100))}%` }}
              aria-hidden="true"
            />
          ) : null}
          <span className="stats-row-key">{item.label ?? item.key}</span>
          <span className="stats-row-value">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function StatsSection({
  title,
  items,
  emptyMessage,
  showBars = true,
  compactRows = false,
}: {
  title: string;
  items: Array<{ key: string; label?: string; value: string; valueNumber?: number }>;
  emptyMessage?: string;
  showBars?: boolean;
  compactRows?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0 && !emptyMessage) return null;

  const maxValue = items.reduce((max, item) => {
    const value = item.valueNumber;
    if (typeof value !== "number" || !Number.isFinite(value)) return max;
    return Math.max(max, value);
  }, 0);
  const collapsible = !compactRows && items.length > COLLAPSED_ROWS;
  const visibleItems = collapsible && !expanded ? items.slice(0, COLLAPSED_ROWS) : items;

  return (
    <section className="stats-section">
      <header className="stats-section-header">
        <h2 className="stats-section-title">{title}</h2>
      </header>

      {items.length === 0 ? (
        <div className="empty-state">{emptyMessage}</div>
      ) : (
        <div className={`stats-list${compactRows ? " stats-list-compact" : ""}`} role="list">
          {visibleItems.map((item) => (
            <div key={item.key} className={`stats-row${compactRows ? " stats-row-compact" : ""}`} role="listitem">
              {showBars && typeof item.valueNumber === "number" && Number.isFinite(item.valueNumber) && maxValue > 0 ? (
                <div
                  className="stats-row-bar"
                  style={{ width: `${Math.max(0, Math.min(100, (item.valueNumber / maxValue) * 100))}%` }}
                  aria-hidden="true"
                />
              ) : null}
              <span className="stats-row-key">{item.label ?? item.key}</span>
              <span className="stats-row-value">{item.value}</span>
            </div>
          ))}
        </div>
      )}
      {collapsible && (
        <button type="button" className="stats-section-more" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Show fewer" : `Show all ${items.length}`}
        </button>
      )}
    </section>
  );
}

function formatGaugeValue(gaugeId: string, value: number): string {
  if (gaugeId === "most_ground_kills_in_sortie" || gaugeId === "most_air_kills_in_sortie") {
    return Math.round(value).toLocaleString();
  }

  if (gaugeId === "longest_refuel_contact_seconds") {
    return `${Math.round(value)}s`;
  }

  if (gaugeId === "longest_missile_hit_nm") {
    return `${value.toFixed(1)} nm`;
  }

  if (gaugeId === "highest_speed_mach") {
    return `Mach ${value.toFixed(2)}`;
  }

  if (gaugeId === "highest_altitude_ft") {
    return `${Math.round(value).toLocaleString()} ft`;
  }

  if (gaugeId === "longest_sortie_distance_nm" || gaugeId === "longest_noe_distance_nm") {
    return `${value.toFixed(1)} nm`;
  }

  return String(value);
}

export default PilotStatsTab;
